/** ログイン状態を画面へ配る入れ物（画面は `useSession()` で受け取る）。 */
import { createContext, useContext } from 'react';
import type { Branding, Me, RolesResponse } from '../api/types.js';
import type { Grant } from '../menu/visible.js';

/**
 * `unreachable` … 開き直した瞬間にサーバへつながらなかった（ログインは残してある・issue #363）。
 * 「もう一度読み込む」（[SessionValue.retryRestore]）で読み込み直す。
 */
export type SessionStatus = 'loading' | 'signed_out' | 'signed_in' | 'unreachable';

export interface SessionProfile {
  me: Me;
  branding: Branding;
  roles: RolesResponse;
  /** ご自身の役職の「できることの束」 */
  grant: Grant;
  /** ご自身の役職の表示名（画面には役職のキーを出さない） */
  roleDisplayName: string;
}

/** 認証番号を送ったあとの控え（手順2の画面で使う）。 */
export interface LoginChallenge {
  /** この画面の合い言葉（認証番号と一緒に送り返す） */
  challenge: string;
  /** 送り先の電話番号（下4桁だけが見える形） */
  phoneHint: string;
  /** 認証番号が使える秒数 */
  expiresIn: number;
  /** 送り直せるまでの秒数 */
  resendAfter: number;
}

export interface SessionValue {
  status: SessionStatus;
  profile: SessionProfile | null;
  /** ログアウトした理由など、ログイン画面に出す一言 */
  notice: string | null;
  /** 電話番号へ認証番号を送る（手順1）。失敗したときは読める日本語の説明を投げる */
  requestLoginCode: (phone: string) => Promise<LoginChallenge>;
  /** 届いた認証番号を入れてログインする（手順2）。失敗したときは読める日本語の説明を投げる */
  verifyLoginCode: (challenge: string, code: string) => Promise<void>;
  signOut: (notice?: string) => void;
  /** 開き直したときの読み込みをやり直す（つながらなかったとき・issue #363） */
  retryRestore: () => void;
}

export const SessionContext = createContext<SessionValue | null>(null);

export function useSession(): SessionValue {
  const session = useContext(SessionContext);
  if (!session) {
    throw new Error('ログイン状態が用意されていません');
  }
  return session;
}
